import * as React from 'react';
import { useIntl } from 'react-intl';
import { MenuItem } from 'lib/types';
import { sortMenuItems } from 'lib/firebase/util';
import { useDispatch, useSelector } from 'react-redux';
import { isMobileMenuOpen } from 'core/redux/menu/selectors';
import classnames from 'classnames';
import { globalHistory } from '@reach/router';
import { closeMobileMenu } from 'core/redux/menu/actions';
import DefaultLogo from 'assets/logoWithText.inline.svg';
import useWindowSize from 'core/hooks/useWindowSize';
import StartBookingButton from 'booking/components/StartBookingButton';
import DefaultLine from 'assets/line.inline.svg';
import NavLink from '../NavLink';
import * as styles from './Nav.module.scss';
import OpenMenuButton from '../OpenMenuButton';
import CloseMenuButton from '../CloseMenuButton';
import NavLogo from '../NavLogo';
import ExternalLink from '../ExternalLink';
import CallToAction from '../CallToAction';
import useClientAppUrl from '../../hooks/useClientAppUrl';
import useLgBreakpoint from '../../hooks/useLgBreakpoint';
import { BREAKPOINT_XL_MIN } from '../../constants';

type NavProps = {
    prefix: string;
    items: ReadonlyArray<MenuItem>;
    logoImage?: React.ReactNode;
    logoLink: string;
    showCTA?: boolean;
    classes?: string;
    hideActionButtons?: boolean;
    bookingCitySlug?: string;
    bookingLocationSlug?: string;
    isCareerPage: boolean;
};

function createLinks(
    prefix: string,
    items: ReadonlyArray<MenuItem>,
    hidden: boolean,
    submenuOpen: number | null,
    setSubmenuOpen: (val: number | null) => void,
): ReadonlyArray<React.ReactNode> {
    return sortMenuItems(items).map(
        (
            { id, sortOrder, to, external, label, subItems }: MenuItem,
            idx: number,
        ) => (
            <NavLink
                to={external ? to : `${prefix}${to}`}
                external={external}
                label={label}
                submenuOpen={submenuOpen === idx}
                toggleSubmenu={() =>
                    setSubmenuOpen(submenuOpen === idx ? null : idx)
                }
                subItems={subItems}
                tabIndex={hidden ? -1 : 0}
                key={[id, (sortOrder || idx).toString(10)].join('-')}
            />
        ),
    );
}

function newCareerCreateLinks(
    prefix: string,
    hidden: boolean,
    submenuOpen: number | null,
    setSubmenuOpen: (val: number | null) => void,
    isSmallScreen?: boolean,
): ReadonlyArray<React.ReactNode> {
    const moreSubItems: MenuItem[] = [
        {
            id: 'vi7vechos',
            external: false,
            to: '/students-new-grads',
            label: 'Students & New Grads',
            boldLabel: '',
            sortOrder: 0,
            subItems: [],
        },
        {
            id: '1m2687zui',
            external: true,
            to: '/medical-excellence',
            label: 'Medical Excellence',
            boldLabel: '',
            sortOrder: 1,
            subItems: [],
        },
        {
            id: '3omiang6n',
            external: false,
            to: '/life-at-bond',
            label: 'Life at Bond',
            boldLabel: '',
            sortOrder: 2,
            subItems: [],
        },
    ];
    const newMenuItems: ReadonlyArray<MenuItem> = [
        {
            id: '56dh0l5ta',
            external: false,
            to: '/veterinarians',
            label: 'Veterinarians',
            boldLabel: '',
            sortOrder: 1,
            subItems: [],
        },
        {
            id: '5s1j50wk0',
            external: false,
            to: '/vet-nurses',
            label: 'Vet Nurses',
            boldLabel: '',
            sortOrder: 2,
            subItems: [],
        },
        {
            id: '9zc10q79z',
            external: false,
            to: '/our-clinics',
            label: 'Our Clinics',
            boldLabel: '',
            sortOrder: 3,
            subItems: [],
        },
        {
            id: '95a607ij2',
            external: false,
            to: '/benefits',
            label: 'Benefits',
            boldLabel: '',
            sortOrder: 4,
            subItems: [],
        },
        ...(isSmallScreen
            ? [
                  ...moreSubItems.map((item, index) => ({
                      ...item,
                      sortOrder: 4 + index,
                  })),
                  {
                      id: 'open-positions',
                      external: false,
                      to: '/open-positions',
                      label: 'Open Positions',
                      boldLabel: '',
                      sortOrder: 0,
                      subItems: [],
                  },
              ]
            : [
                  {
                      id: 'r262iw6fw',
                      external: false,
                      to: '/',
                      label: 'More',
                      boldLabel: '',
                      sortOrder: 5,
                      subItems: moreSubItems,
                  },
              ]),
    ];

    return sortMenuItems(newMenuItems).map(
        (
            { id, sortOrder, to, external, label, subItems }: MenuItem,
            idx: number,
        ) => (
            <NavLink
                to={external ? to : `${prefix}${to}`}
                external={external}
                label={label}
                submenuOpen={submenuOpen === idx}
                toggleSubmenu={() =>
                    setSubmenuOpen(submenuOpen === idx ? null : idx)
                }
                subItems={subItems}
                tabIndex={hidden ? -1 : 0}
                key={[id, (sortOrder || idx).toString(10)].join('-')}
            />
        ),
    );
}

export default function Nav({
    prefix,
    items,
    children,
    logoImage,
    logoLink,
    showCTA,
    hideActionButtons = false,
    classes,
    bookingCitySlug,
    bookingLocationSlug,
    isCareerPage,
}: React.PropsWithChildren<NavProps>) {
    const isOpen = useSelector(isMobileMenuOpen);
    const isSmallScreen = useLgBreakpoint();
    const smallScreenHidden = isSmallScreen && !isOpen;
    const [submenuOpen, setSubmenuOpen] = React.useState<number | null>(null);
    const links = React.useMemo(
        () =>
            createLinks(
                prefix,
                items,
                smallScreenHidden,
                submenuOpen,
                setSubmenuOpen,
            ),
        [prefix, items, smallScreenHidden, submenuOpen],
    );
    const newLinks = React.useMemo(
        () =>
            newCareerCreateLinks(
                prefix,
                smallScreenHidden,
                submenuOpen,
                setSubmenuOpen,
                isSmallScreen,
            ),
        [prefix, smallScreenHidden, submenuOpen, isSmallScreen],
    );
    const { formatMessage } = useIntl();
    const spacer = React.useRef<HTMLDivElement>(null);
    // TODO this isn't ever set to true
    const [isTransitioning, setIsTransitioning] =
        React.useState<boolean>(false);
    const [viewportInnerHeight, setViewportInnerHeight] = React.useState(0);
    const dispatch = useDispatch();
    const clientAppLink = useClientAppUrl();

    const { height } = useWindowSize();

    React.useEffect(() => {
        let handle: number | undefined;
        setViewportInnerHeight(window.innerHeight);

        const handler = () => {
            setViewportInnerHeight(window.innerHeight);

            handle = requestAnimationFrame(() => {
                handle = requestAnimationFrame(() => {
                    handle = undefined;

                    const contentHeight =
                        window.visualViewport?.height || height;

                    setViewportInnerHeight(contentHeight);
                });
            });
        };

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handler);
        }

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handler);
            }

            if (handle !== undefined) {
                cancelAnimationFrame(handle);
            }
        };
    }, [height]);

    React.useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > BREAKPOINT_XL_MIN && isOpen) {
                dispatch(closeMobileMenu());
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [dispatch, isOpen]);

    // Close Menu after navigation
    React.useEffect(() => {
        return globalHistory.listen(({ action }) => {
            if (action === 'PUSH') {
                dispatch(closeMobileMenu());
            }
        });
    }, [dispatch]);

    const onTransitionEnd = () => {
        if (!isOpen) {
            setIsTransitioning(false);
        }
    };

    React.useEffect(() => {
        let observer: IntersectionObserver | null;
        if (spacer.current) {
            observer = new IntersectionObserver(
                ([entry]: IntersectionObserverEntry[]) => {
                    if (entry.isIntersecting) {
                        document.documentElement.classList.remove('scrolling');
                    } else {
                        document.documentElement.classList.add('scrolling');
                    }
                },
                {
                    root: null,
                    threshold: 1.0,
                },
            );

            observer.observe(spacer.current);
        }

        return () => {
            if (observer) {
                observer.disconnect();
            }
        };
    }, [spacer]);

    // scroll to top when user clicks the nav bar
    const handleNavClick = React.useCallback(() => {
        spacer.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    return (
        <>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <header
                id="page-header"
                className={classnames(styles.root, classes)}
                onClick={handleNavClick}
            >
                <NavLogo
                    fadeEffect
                    to={logoLink}
                    logo={logoImage}
                    label={formatMessage({ id: 'nav.home' })}
                />
                {isCareerPage && (
                    <div className={styles.careersContainer}>
                        <DefaultLine aria-hidden="true" />
                        <span className={styles.careersText}>CAREERS</span>
                    </div>
                )}
                <OpenMenuButton />
                <nav
                    className={classnames(styles.nav, {
                        [styles.transitioning]: isTransitioning,
                    })}
                    itemType="https://schema.org/SiteNavigationElement"
                    aria-label="Main Navigation"
                    style={
                        {
                            '--mobile-browser-inner-height': `${viewportInnerHeight}px`,
                        } as React.CSSProperties
                    }
                >
                    <ul
                        onTransitionEnd={onTransitionEnd}
                        role="menu"
                        aria-hidden={smallScreenHidden}
                        id="main-nav"
                    >
                        {isCareerPage ? newLinks : links}
                        {showCTA && !isCareerPage && (
                            <li
                                role={isSmallScreen ? 'menuitem' : 'none'}
                                className={styles.bookingLink}
                            >
                                <CallToAction
                                    className={classnames(
                                        styles.mobile,
                                        styles.loginButton,
                                    )}
                                    Component={ExternalLink}
                                    variant="outlined"
                                    href={clientAppLink}
                                    tabIndex={smallScreenHidden ? -1 : 0}
                                    role="menuitem"
                                >
                                    <span>Log In</span>
                                </CallToAction>
                            </li>
                        )}
                    </ul>
                </nav>
                {children}
                {hideActionButtons || (
                    <div className={styles.portalButtons}>
                        {!isCareerPage && (
                            <CallToAction
                                className={classnames(
                                    styles.desktop,
                                    styles.loginButton,
                                )}
                                Component={ExternalLink}
                                size="small"
                                variant="text"
                                href={clientAppLink}
                                aria-hidden={isSmallScreen}
                                tabIndex={isSmallScreen ? -1 : 0}
                            >
                                <span>Log In</span>
                            </CallToAction>
                        )}
                        {!isCareerPage ? (
                            <StartBookingButton
                                size="small"
                                className={styles.bookingCta}
                                citySlug={bookingCitySlug}
                                locationSlug={bookingLocationSlug}
                            >
                                <span>BOOK NOW</span>
                            </StartBookingButton>
                        ) : !isSmallScreen ? (
                            <CallToAction
                                size="small"
                                className={styles.openPositions}
                            >
                                <span>Open Positions</span>
                            </CallToAction>
                        ) : null}
                    </div>
                )}
                <CloseMenuButton />
            </header>
            <div className={styles.spacer} ref={spacer} />
        </>
    );
}

Nav.defaultProps = {
    prefix: '',
    logoImage: <DefaultLogo role="img" aria-label="Bond Vet logo" />,
};
